import type { OrderSummary } from '@/infrastructure/OMSClient';
import type {
  FulfillmentOrderLine,
  FulfillmentPendingParcel,
  FulfillmentTracking,
} from '@/infrastructure/FulfillmentAdminClient';

export type OutboundParcel = FulfillmentTracking | FulfillmentPendingParcel;
export const isPendingParcel = (parcel: OutboundParcel): parcel is FulfillmentPendingParcel => 'bookingId' in parcel;

/** Deleo has no label archive: draw legacy waybills or DELEONEX DL/ML references, never its courier waybill. */
export const DELEO_CARRIER = 'DELEO';
export const DELEONEX_CARRIER = 'DELEONEX';
export const DELEONEX_REFERENCE_PENDING = 'Deleonex reference pending — sticker not available yet';
export const DELEONEX_BOOKING_PENDING = 'Deleonex carrier booking pending — sticker not available yet';
export const DELEONEX_NEEDS_ATTENTION =
  'Deleonex booking needs attention — it may be held or cancelled at Deleo; sticker printing and outbound are blocked';

/** What the last product on the sticker says after its name when the box carries a sample set — the carrier's declaration says the same [10/13]. */
export const WITH_SAMPLE_SET = 'with sample set';

/** How many lines the sticker lists before it folds the rest into one "more" row. */
const STICKER_LINES = 3;

/** The waybill is a 15x10 sticker; anything else and the carrier's scanner has nothing to read. */
export const DELEO_LABEL_PAGE_STYLE = `
  @page { size: 150mm 100mm !important; margin: 0 !important; }
  @media print {
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    html, body { margin: 0 !important; padding: 0 !important; }
    .print-area, .deleo-verification-page {
      width: 15cm !important;
      height: 10cm !important;
      margin: 0 !important;
      box-sizing: border-box !important;
      page-break-after: always !important;
    }
  }
`;

/** Both Deleo platforms use the same Admin-drawn sticker, not an archived label. */
export const isDeleoParcel = (row: Pick<OutboundParcel, 'carrier'>): boolean =>
  [DELEO_CARRIER, DELEONEX_CARRIER].includes((row.carrier ?? '').trim().toUpperCase());

export const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

export const asText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export const pendingParcelsOf = (payload: Record<string, unknown>): FulfillmentPendingParcel[] =>
  Array.isArray(payload['pendingParcels']) ? (payload['pendingParcels'] as FulfillmentPendingParcel[]) : [];

/** Deleonex number is the courier waybill; only its separate Deleo reference belongs on the sticker. */
export const deleoLabelNumberOf = (parcel: OutboundParcel | null | undefined): string =>
  (parcel?.carrier ?? '').trim().toUpperCase() === DELEONEX_CARRIER
    ? (asText(parcel?.referenceNo) ?? '')
    : parcel && !isPendingParcel(parcel)
      ? (parcel.number ?? '')
      : '';

export const deleoLabelRefusalOf = (parcel: OutboundParcel | null | undefined): string => {
  if ((parcel?.carrier ?? '').trim().toUpperCase() !== DELEONEX_CARRIER) {
    return '';
  }
  if (parcel && isPendingParcel(parcel) && parcel.needsAttention !== false) {
    return DELEONEX_NEEDS_ATTENTION;
  }
  if (!deleoLabelNumberOf(parcel).trim()) {
    return DELEONEX_REFERENCE_PENDING;
  }
  return asText(parcel?.carrierOrderId) ? '' : DELEONEX_BOOKING_PENDING;
};

/** Detail and print payloads also carry inbound LINE refs; only this order's outbound parcel can ship. */
export const isOutboundParcel = (row: FulfillmentTracking, orderId: number): boolean =>
  row.refId === String(orderId) && (row.kind == null || row.kind === 'OUTBOUND');

/** Among this order's outbound parcels, use the newest timestamp when known, otherwise the last API row. */
export const currentOutboundParcelOf = (
  tracking: FulfillmentTracking[],
  orderId: number,
): FulfillmentTracking | null => {
  const parcels = tracking.filter((row) => isOutboundParcel(row, orderId));
  return (
    parcels.reduce<FulfillmentTracking | null>((selected, candidate) => {
      if (!selected) {
        return candidate;
      }
      const selectedAt = Date.parse(selected.createdAt ?? '');
      const candidateAt = Date.parse(candidate.createdAt ?? '');
      if (Number.isFinite(candidateAt)) {
        return !Number.isFinite(selectedAt) || candidateAt >= selectedAt ? candidate : selected;
      }
      return Number.isFinite(selectedAt) ? selected : candidate;
    }, null) ??
    parcels[parcels.length - 1] ??
    null
  );
};

/** A pending parcel is Facade's current booking; tracking rows remain history until Facade omits it. */
export const currentOutboundShipmentOf = (
  tracking: FulfillmentTracking[],
  orderId: number,
  pendingParcels: FulfillmentPendingParcel[] = [],
): OutboundParcel | null =>
  pendingParcels.find(
    (parcel) => parcel?.localOrderId === String(orderId) && asText(parcel.carrier)?.toUpperCase() === DELEONEX_CARRIER,
  ) ?? currentOutboundParcelOf(tracking, orderId);

export const deleoTrackingOf = (
  tracking: FulfillmentTracking[],
  orderId: number,
  pendingParcels: FulfillmentPendingParcel[] = [],
): OutboundParcel | null => {
  const parcel = currentOutboundShipmentOf(tracking, orderId, pendingParcels);
  return parcel && isDeleoParcel(parcel) ? parcel : null;
};

const asCount = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

interface DeleoLabelSource {
  orderId: number;
  shipping: unknown;
  lines: FulfillmentOrderLine[];
  tracking: FulfillmentTracking[];
  pendingParcels?: FulfillmentPendingParcel[];
  parcel?: OutboundParcel;
  /** A marketing order carries no sample set, so its sticker says nothing of one. */
  isMarketing?: boolean;
}

/**
 * Adapts a 2.0 order to the OMS label sheets, so it prints the selected Deleo-family sticker.
 * The sample set is no line of the sticker: it rides on the last product's name, the way the carrier's
 * declaration says it, while the PACKING sheet behind lists it apart [10/13].
 */
export const toDeleoLabelOrder = ({
  orderId,
  shipping,
  lines,
  tracking,
  pendingParcels,
  parcel: selectedParcel,
  isMarketing = false,
}: DeleoLabelSource): OrderSummary => {
  const parcel = selectedParcel ?? deleoTrackingOf(tracking, orderId, pendingParcels);
  const stickerNumber = deleoLabelRefusalOf(parcel) ? '' : deleoLabelNumberOf(parcel);
  const consignee = asRecord(asRecord(shipping)?.['consignee']);
  const products = lines.filter((line) => !line.isSample);

  return {
    orderId: String(orderId),
    consignee: consignee ? { countryCode: asText(consignee['countryCode']) } : undefined,
    deleoData: {
      deleoOrderId: parcel ? (isPendingParcel(parcel) ? parcel.localOrderId : parcel.refId) : String(orderId),
      deleoTrackingNo: stickerNumber,
    },
    // The sheets print what the picker reads off the shelf: the ordered name and the ordered count.
    lineItems: products.map((line, index) => {
      const name = asText(line.productName) ?? line.sku;
      const nameKor = asText(line.productNameKr) ?? '';
      const carriesSampleSet = !isMarketing && index === products.length - 1;
      return {
        name: carriesSampleSet ? `${name} ${WITH_SAMPLE_SET}` : name,
        nameKor: carriesSampleSet && nameKor ? `${nameKor} ${WITH_SAMPLE_SET}` : nameKor,
        quantity: asCount(line.totalQuantity),
      };
    }),
  } as unknown as OrderSummary;
};

/** Said on the sticker's folded row when the line that carries "with sample set" is one the sticker does not list. */
export const deleoLabelMoreNote = (order: OrderSummary): string | undefined => {
  const items = order.lineItems ?? [];
  const last = items[items.length - 1] as { name?: string; nameKor?: string } | undefined;
  if (!last || items.length <= STICKER_LINES) {
    return undefined;
  }
  return (last.nameKor || last.name || '').endsWith(WITH_SAMPLE_SET) ? WITH_SAMPLE_SET : undefined;
};
